import { clientEnv } from "../env";

export interface OneCorePlan {
  document: { title: string; revision: string; sourceDate: string; incorporatedDate: string; source: string; transcription: string; scope: string };
  sections: { id: string; title: string; sourceStatus: string; content: string }[];
  decisions: { id: string; references: string[]; current: string; delivery: string }[];
  implementation: { id: string; title: string; status: string; detail: string; evidence: string[]; pending: string }[];
  sourceText: string;
}

export interface ProductInventory {
  oneCore?: OneCorePlan;
  schemaVersion: number;
  document: {
    title: string;
    revision: string;
    cutoffDate: string;
    timezone: string;
    scope: string;
    pendingSource: string;
  };
  statuses: Record<string, string>;
  sections: { id: string; title: string; sourceStatus: string; content: string }[];
  verification: { kind: "documentary_reference"; capabilitiesAudited: false; note: string };
  summary: { sectionCount: number; incidentCount: number };
  incidents: { id: string; description: string; closureStatus: "not_verified"; historical: boolean }[];
}

export async function fetchProductInventory(
  sessionToken: string,
  signal: AbortSignal
): Promise<ProductInventory> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/creator/inventory`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
    cache: "no-store",
    signal
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error("Inicia sesión de nuevo para consultar el inventario.");
    if (response.status === 403) throw new Error("El inventario está reservado a la cuenta Creador.");
    if (response.status === 404) throw new Error("El inventario todavía no está disponible en este servidor.");
    throw new Error("No se pudo cargar el inventario. Puedes volver a intentarlo.");
  }
  const data = await response.json() as ProductInventory;
  if (data.schemaVersion !== 1 || !Array.isArray(data.sections) || !data.document || !data.summary) {
    throw new Error("El servidor devolvió un inventario incompatible.");
  }
  return data;
}

export function inventoryAsText(data: ProductInventory): string {
  return [
    data.document.title,
    `Revisión documental: ${data.document.revision} · Corte: ${data.document.cutoffDate}`,
    data.document.scope,
    data.verification.note,
    "ESTADOS",
    ...Object.entries(data.statuses).map(([name, description]) => `${name}: ${description}`),
    "ÍNDICE",
    ...data.sections.map((section) => `${section.id}. ${section.title}`),
    ...data.sections.map((section) =>
      `${section.id}. ${section.title}\nESTADO: ${section.sourceStatus}\n\n${section.content}`
    ),
    ...(data.document.pendingSource ? [`FUENTE PENDIENTE: ${data.document.pendingSource}`] : []),
    "FIN DEL INVENTARIO · OMNISYNIA — One Intelligence"
  ].join("\n\n");
}
